import React, { useState, useEffect } from 'react';
import { buildApiUrl } from '../config';

/**
 * TeacherNuggetAnalytics Component
 * 
 * Displays which students accessed which nuggets in a course,
 * and their progress/scores out of 100.
 * 
 * Can be used standalone or embedded as a tab in the Courses module.
 */
const TeacherNuggetAnalytics = ({ courseId, courseName, teacherEmail, onBack, isEmbedded = false, courses = [] }) => {
  const [studentAccess, setStudentAccess] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCourse, setSelectedCourse] = useState(courseId);
  const [selectedNugget, setSelectedNugget] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentAnswers, setStudentAnswers] = useState([]);
  const [showAnswerDetails, setShowAnswerDetails] = useState(false);
  const [availableCourses, setAvailableCourses] = useState(courses);

  // Load all students accessing nuggets in selected course
  useEffect(() => {
    if (!selectedCourse) {
      if (!isEmbedded) {
        setError('No course selected');
      }
      setLoading(false);
      return;
    }

    loadStudentAccess();
  }, [selectedCourse]);

  // Load courses if not provided
  useEffect(() => {
    if (!isEmbedded && availableCourses.length === 0) {
      loadTeacherCourses();
    }
  }, []);

  const loadTeacherCourses = async () => {
    try {
      const response = await fetch(buildApiUrl(`/api/courses?teacher_email=${encodeURIComponent(teacherEmail)}`));
      if (response.ok) {
        const data = await response.json();
        setAvailableCourses(data);
      }
    } catch (error) {
      console.error('Error loading courses:', error);
    }
  };

  const loadStudentAccess = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(
        buildApiUrl(`/api/courses/${selectedCourse}/nuggets-student-access?teacher_email=${encodeURIComponent(teacherEmail)}`)
      );
      
      if (!response.ok) {
        throw new Error('Failed to load student access data');
      }
      
      const data = await response.json();
      setStudentAccess(data.student_access || []);
    } catch (err) {
      setError(err.message);
      console.error('Error loading student access:', err);
    } finally {
      setLoading(false);
    }

  };

  const loadStudentAnswers = async (nuggetId, studentEmail) => {
    try {
      const response = await fetch(
        buildApiUrl(`/api/nuggets/${nuggetId}/student-answers?student_email=${encodeURIComponent(studentEmail)}`)
      );
      
      if (!response.ok) {
        throw new Error('Failed to load student answers');
      }
      
      const data = await response.json();
      setStudentAnswers(data.answers || []);
      setShowAnswerDetails(true);
    } catch (err) {
      console.error('Error loading student answers:', err);
      setError(err.message);
    }
  };

  // Group students by nugget
  const nuggetGroups = studentAccess.reduce((acc, record) => {
    const nuggetKey = record.nugget_id;
    if (!acc[nuggetKey]) {
      acc[nuggetKey] = {
        nugget_id: record.nugget_id,
        nugget_name: record.nugget_name,
        students: []
      };
    }
    acc[nuggetKey].students.push(record);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading student progress...</div>
      </div>
    );
  }

  // Embedded mode (shown as tab in Courses)
  if (isEmbedded) {
    return (
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-teal-200/50">
        {/* Course Selector for Embedded Mode */}
        {availableCourses.length > 0 && (
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Select Course:</label>
            <select
              value={selectedCourse || ''}
              onChange={(e) => setSelectedCourse(e.target.value ? parseInt(e.target.value) : null)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none"
            >
              <option value="">-- Choose a course --</option>
              {availableCourses.map(course => (
                <option key={course.id} value={course.id}>
                  {course.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {!selectedCourse ? (
          <div className="text-center py-12 text-gray-500">
            <p>Select a course to view student progress.</p>
          </div>
        ) : Object.keys(nuggetGroups).length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p>No student access data yet for this course.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.values(nuggetGroups).map(nugget => (
              <div key={nugget.nugget_id} className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Nugget Header */}
                <div className="bg-gradient-to-r from-teal-50 to-teal-100 p-4 border-b border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900">{nugget.nugget_name}</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {nugget.students.length} student{nugget.students.length !== 1 ? 's' : ''} accessed
                  </p>
                </div>

                {/* Student Table - Compact */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Student</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Access Time</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Score</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Status</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {nugget.students.map((student, idx) => (
                        <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50">
                          <td className="px-4 py-2 text-gray-900">{student.student_name}</td>
                          <td className="px-4 py-2 text-gray-600 text-xs">
                            {new Date(student.accessed_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-2">
                              <div className="w-16 bg-gray-200 rounded-full h-1.5">
                                <div
                                  className="bg-teal-500 h-1.5 rounded-full"
                                  style={{
                                    width: `${(student.final_score || 0) / (student.total_marks || 100) * 100}%`
                                  }}
                                />
                              </div>
                              <span className="text-xs font-semibold">
                                {(student.final_score || 0).toFixed(0)}/100
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-2">
                            <span
                              className={`px-2 py-0.5 rounded text-xs font-medium ${
                                student.is_completed
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}
                            >
                              {student.is_completed ? '✓' : '⏳'}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-teal-600 hover:text-teal-800 font-medium text-xs cursor-pointer"
                            onClick={() => {
                              setSelectedStudent(student);
                              setSelectedNugget(nugget);
                              loadStudentAnswers(nugget.nugget_id, student.student_email);
                            }}
                          >
                            View
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Standalone mode (full page view)
  return (
    <div className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className="mb-4 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
        >
          ← Back
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Student Progress Analytics</h1>
        <p className="text-gray-600 mt-2">Course: {courseName}</p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {Object.keys(nuggetGroups).length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p>No student access data yet for this course.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.values(nuggetGroups).map(nugget => (
            <div key={nugget.nugget_id} className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Nugget Header */}
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900">{nugget.nugget_name}</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {nugget.students.length} student{nugget.students.length !== 1 ? 's' : ''} accessed
                </p>
              </div>

              {/* Student Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Student Name</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Access Time</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Questions Answered</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Score</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nugget.students.map((student, idx) => (
                      <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-900">{student.student_name}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {new Date(student.accessed_at).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {student.answers_count || 0}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <div className="w-24 bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-green-500 h-2 rounded-full transition-all"
                                style={{
                                  width: `${(student.final_score || 0) / (student.total_marks || 100) * 100}%`
                                }}
                              />
                            </div>
                            <span className="text-sm font-semibold text-gray-900">
                              {(student.final_score || 0).toFixed(1)}/100
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium ${
                              student.is_completed
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}
                          >
                            {student.is_completed ? '✓ Completed' : 'In Progress'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <button
                            onClick={() => {
                              setSelectedStudent(student);
                              setSelectedNugget(nugget);
                              loadStudentAnswers(nugget.nugget_id, student.student_email);
                            }}
                            className="text-blue-600 hover:text-blue-800 font-medium"
                          >
                            View Answers
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Student Answers Modal */}
      {showAnswerDetails && selectedStudent && selectedNugget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-96 overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-blue-50 to-blue-100 p-6 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Student Answers</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedStudent.student_name} - {selectedNugget.nugget_name}
                  </p>
                </div>
                <button
                  onClick={() => setShowAnswerDetails(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {studentAnswers.length === 0 ? (
                <p className="text-gray-500">No answers submitted yet.</p>
              ) : (
                <div className="space-y-4">
                  {studentAnswers.map((answer, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="font-semibold text-gray-900">
                            Question {idx + 1}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">{answer.question_text}</p>
                        </div>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            answer.is_correct
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {answer.is_correct ? '✓ Correct' : '✗ Incorrect'}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-gray-600">
                            <strong>Student Answer:</strong> {answer.student_answer}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-600">
                            <strong>Correct Answer:</strong> {answer.correct_answer}
                          </p>
                        </div>
                      </div>
                      <div className="mt-2 pt-2 border-t border-gray-200">
                        <p className="text-sm text-gray-600">
                          <strong>Marks Obtained:</strong> {answer.marks_obtained}/{answer.marks_available}
                        </p>
                      </div>
                    </div>
                  ))}

                  {/* Summary */}
                  <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200 mt-6">
                    <p className="text-lg font-bold text-gray-900">
                      Total Score: {selectedStudent.final_score || 0}/100
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Percentage: {((selectedStudent.final_score || 0) / 100 * 100).toFixed(1)}%
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 p-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowAnswerDetails(false)}
                className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherNuggetAnalytics;
